"use client";

import React from 'react';
import Image from "next/legacy/image";
import { Carousel, CarouselContent, CarouselItem } from "@/components/atoms/carousel";

interface SpeakerType {
    documentId?: string;
    name?: string;
    cargo?: string;
    empresa?: string;
    foto?: {
        url?: string;
        alternativeText?: string;
    };
}

interface EventoSpeakersProps {
    speakers: SpeakerType[];
    title?: string;
}

const EventoSpeakers: React.FC<EventoSpeakersProps> = ({ speakers, title = 'Speakers' }) => {
    const [api, setApi] = React.useState<any>();
    const [current, setCurrent] = React.useState(0);
    const [count, setCount] = React.useState(0);

    React.useEffect(() => {
        if (!api) {
            return;
        }

        setCount(api.scrollSnapList().length);
        setCurrent(api.selectedScrollSnap());

        api.on("select", () => {
            setCurrent(api.selectedScrollSnap());
        });
    }, [api]);

    if (!speakers || speakers.length === 0) {
        return null;
    }

    return (
        <section className="mb-12 sm:mb-16 lg:mb-20 relative w-full pt-4 sm:pt-6 lg:pt-8 pb-4 sm:pb-6 lg:pb-8 h-full bg-stone-300/20">
            <div className="relative w-full flex pb-20 sm:pb-32 lg:pb-40">

                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16  z-10">
                    <h2 className="font-exo-2 text-2xl sm:text-3xl lg:text-4xl font-semibold uppercase text-left mb-6 sm:mb-8 lg:mb-10">
                        {title}
                    </h2>                    {/* Slider de Speakers */}
                    <div className="relative w-full">
                        <Carousel
                            setApi={setApi}
                            opts={{
                                align: "end",
                                loop: true,
                                skipSnaps: false,
                                dragFree: true,
                            }}
                            className="w-full"
                        >
                            <CarouselContent className="-ml-2 md:-ml-4">
                                {speakers.map((speaker, index) => (
                                    <CarouselItem
                                        key={`speaker-${speaker.documentId || index}`}
                                        className="pl-2 md:pl-4 basis-[280px] sm:basis-[320px] lg:basis-[360px]"
                                    >
                                        <div className="w-full aspect-[4/5] relative rounded-xl overflow-hidden shadow-sm transition-transform hover:scale-105 hover:shadow-md">
                                            {/* Imagem de fundo */}
                                            {speaker.foto?.url ? (
                                                <Image
                                                    src={speaker.foto.url}
                                                    alt={speaker.name || "Speaker"}
                                                    layout="fill"
                                                    className="object-cover"
                                                    sizes="(max-width: 640px) 280px, (max-width: 1024px) 320px, 360px"
                                                    style={{ objectPosition: 'center top' }}
                                                />
                                            ) : (
                                                <div className="absolute inset-0 bg-zinc-300 flex items-center justify-center">
                                                    <div className="text-zinc-500 text-center">
                                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mx-auto mb-2">
                                                            <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="currentColor" />
                                                        </svg>
                                                        <span className="text-sm">Sem foto</span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Overlay gradiente */}
                                            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />

                                            {/* Texto sobreposto */}
                                            <div className="absolute inset-x-0 bottom-0 pb-4 sm:pb-5">
                                                <div className="px-5">
                                                    <h3 className="text-[#F7941D] text-lg sm:text-xl font-bold font-sans leading-tight line-clamp-2">
                                                        {speaker.name || "Nome não informado"}
                                                    </h3>
                                                    <p className="text-zinc-200 text-xs sm:text-sm font-medium font-sans  leading-tight line-clamp-2 mt-1">
                                                        {speaker.cargo || "Não informado"}
                                                    </p>
                                                    <p className="text-zinc-200 text-xs sm:text-sm font-medium font-sans  leading-tight line-clamp-2">
                                                        {speaker.empresa || "Não informado"}
                                                    </p>

                                                     <p className="text-zinc-200 text-xs sm:text-sm font-light font-sans  leading-tight line-clamp-2 mt-2 border px-2 py-1 max-w-fit flex">
                                                        {speaker.cargo || "Não informado"}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </CarouselItem>
                                ))}
                            </CarouselContent>

                           
                        </Carousel>

                        {/* Navegação com pontos - alinhada à esquerda */}
                        {speakers.length > 1 && count > 1 && (
                            <div className="flex justify-start mt-6 sm:mt-8">
                                <div className="flex space-x-2">
                                    {Array.from({ length: count }).map((_, index) => (
                                        <button
                                            key={index}
                                            className={`ml-4 w-8 h-2 rounded-full transition-all duration-300 ${
                                                index === current 
                                                    ? 'w-14 bg-sky-600 scale-125' 
                                                    : 'bg-zinc-300 hover:bg-zinc-400'
                                            }`}
                                            onClick={() => api?.scrollTo(index)}
                                            aria-label={`Ir para slide ${index + 1}`}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default EventoSpeakers;
